import type { Snapshot, SnapshotGame } from '../data/snapshotTypes'
import type { Game, GameResult } from './types'

/**
 * Where a game's score comes from.
 *
 * For a fixture-linked game the answer is PlayHQ, and the app never stores its
 * own copy: the score is looked up from the snapshot by `playhqGameId` every
 * time it is displayed. That is what makes the merge in the Step 3 brief a
 * non-event -- there is nothing to reconcile, because there is only ever one
 * source. A re-sync that corrects a score corrects it everywhere at once.
 *
 * A manual game has no fixture and therefore no source, so it keeps the score
 * the scorer typed. Without that exception a manual game could never have a
 * result at all, and every wins-vs-losses split in Stats would quietly ignore it.
 */
export type ResultSource = 'playhq' | 'manual' | 'pending' | 'none'

export interface ResolvedResult {
  source: ResultSource
  ourScore: number | null
  theirScore: number | null
  result: GameResult | null
  /** PlayHQ's own status, when we have it: FINAL, UPCOMING, CANCELLED... */
  playhqStatus: SnapshotGame['status'] | null
  /** True when this game is waiting on PlayHQ rather than missing a score. */
  awaitingResult: boolean
}

const NOTHING: ResolvedResult = {
  source: 'none',
  ourScore: null,
  theirScore: null,
  result: null,
  playhqStatus: null,
  awaitingResult: false,
}

export function resolveResult(game: Game, snapshot: Snapshot | null): ResolvedResult {
  // Manual game: whatever was entered is all there is.
  if (!game.playhqGameId) {
    if (game.ourScore == null || game.theirScore == null) {
      return { ...NOTHING, source: game.status === 'final' ? 'none' : 'none' }
    }
    return {
      source: 'manual',
      ourScore: game.ourScore,
      theirScore: game.theirScore,
      result: compare(game.ourScore, game.theirScore),
      playhqStatus: null,
      awaitingResult: false,
    }
  }

  const fixture = snapshot?.games.find((g) => g.playhq_game_id === game.playhqGameId) ?? null
  if (!fixture) {
    // We know it is a real fixture but have no snapshot for it yet.
    return { ...NOTHING, source: 'pending', awaitingResult: true }
  }

  if (fixture.status === 'FINAL' && fixture.our_score != null && fixture.their_score != null) {
    return {
      source: 'playhq',
      ourScore: fixture.our_score,
      theirScore: fixture.their_score,
      result: fixture.result ?? compare(fixture.our_score, fixture.their_score),
      playhqStatus: fixture.status,
      awaitingResult: false,
    }
  }

  return {
    source: 'pending',
    ourScore: null,
    theirScore: null,
    result: null,
    playhqStatus: fixture.status,
    awaitingResult: fixture.status !== 'CANCELLED',
  }
}

function compare(ours: number, theirs: number): GameResult {
  if (ours > theirs) return 'W'
  if (ours < theirs) return 'L'
  return 'D'
}

/** Short human label for a game's score state. */
export function resultLabel(resolved: ResolvedResult): string {
  if (resolved.source === 'playhq' || resolved.source === 'manual') {
    return `${resolved.ourScore}-${resolved.theirScore} ${resolved.result ?? ''}`.trim()
  }
  if (resolved.playhqStatus === 'CANCELLED') return 'Cancelled'
  if (resolved.awaitingResult) return 'Awaiting result'
  return 'No score'
}
